import { jsPDF as JsPDF } from 'jspdf'
import { autoTable } from 'jspdf-autotable'

export interface PdfSection {
    heading?: string;
    rows: Array<{ label: string; value: string }>;
}

export interface PdfTable {
    heading?: string;
    headers: string[];
    rows: string[][];
}

export interface PdfOptions {
    title: string;
    sections?: PdfSection[];
    tables?: PdfTable[];
    footer?: string;
    filename?: string;
}

const MARGIN = 14
const PAGE_WIDTH_MM = 210
const CONTENT_WIDTH = PAGE_WIDTH_MM - MARGIN * 2
const LABEL_WIDTH = 55
const VALUE_WIDTH = CONTENT_WIDTH - LABEL_WIDTH
const LINE_HEIGHT = 5

// jsPDF's built-in fonts use WinAnsi encoding, which has no glyph for some
// Unicode symbols (e.g. the >= and <= signs). Left unhandled, those characters
// corrupt the line they appear on, so replace them with ASCII equivalents
// before rendering.
const CHAR_REPLACEMENTS:Array<[RegExp, string]> = [
    [/≥/g, '>='],
    [/≤/g, '<='],
    [/≠/g, '!='],
    [/→/g, '->'],
    [/←/g, '<-'],
]

function sanitize (text:string):string {
    return CHAR_REPLACEMENTS.reduce(
        (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
        text,
    )
}

function slugify (text:string):string {
    return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export function exportPdf (options:PdfOptions):void {
    const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageHeight = doc.internal.pageSize.getHeight()
    const bottomLimit = pageHeight - MARGIN
    let y = MARGIN

    // Start a new page when the next `needed` mm of content would overflow.
    function ensureSpace (needed:number):void {
        if (y + needed > bottomLimit) {
            doc.addPage()
            y = MARGIN
        }
    }

    // Title
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text(sanitize(options.title), MARGIN, y)
    y += 10

    // Divider line
    doc.setDrawColor(180, 180, 180)
    doc.setLineWidth(0.3)
    doc.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y)
    y += 6

    // Key/value sections
    if (options.sections) {
        for (const section of options.sections) {
            if (section.heading) {
                // Keep the heading with at least its first row.
                ensureSpace(6 + LINE_HEIGHT)
                doc.setFontSize(12)
                doc.setFont('helvetica', 'bold')
                doc.text(sanitize(section.heading), MARGIN, y)
                y += 6
            }
            doc.setFontSize(10)
            for (const row of section.rows) {
                doc.setFont('helvetica', 'normal')
                const lines = doc.splitTextToSize(
                    sanitize(row.value),
                    VALUE_WIDTH,
                )
                const rowHeight = Math.max(1, lines.length) * LINE_HEIGHT
                // Keep a row and its wrapped lines together on one page.
                ensureSpace(rowHeight)
                doc.setFont('helvetica', 'bold')
                doc.text(sanitize(row.label) + ':', MARGIN, y)
                doc.setFont('helvetica', 'normal')
                doc.text(lines, MARGIN + LABEL_WIDTH, y)
                y += rowHeight
            }
            y += 4
        }
    }

    // Tables
    if (options.tables) {
        for (const table of options.tables) {
            if (table.heading) {
                ensureSpace(6 + LINE_HEIGHT)
                doc.setFontSize(12)
                doc.setFont('helvetica', 'bold')
                doc.text(sanitize(table.heading), MARGIN, y)
                y += 6
            }
            autoTable(doc, {
                startY: y,
                head: [table.headers.map(h => sanitize(h))],
                body: table.rows.map(r => r.map(c => sanitize(c))),
                margin: {
                    top: MARGIN,
                    bottom: MARGIN,
                    left: MARGIN,
                    right: MARGIN,
                },
                styles: { fontSize: 9 },
                headStyles: { fillColor: [50, 90, 140] },
            })
            // @ts-expect-error jspdf-autotable adds lastAutoTable to doc
            y = (doc.lastAutoTable?.finalY ?? y) + 6
        }
    }

    // Footer on every page
    if (options.footer) {
        const pageCount = doc.getNumberOfPages()
        doc.setFontSize(8)
        doc.setTextColor(120, 120, 120)
        doc.setFont('helvetica', 'normal')
        for (let p = 1; p <= pageCount; p++) {
            doc.setPage(p)
            doc.text(sanitize(options.footer), MARGIN, pageHeight - 10)
        }
    }

    const filename = options.filename ?? `${slugify(options.title)}.pdf`
    doc.save(filename)
}
